<template>
  <ContainerInfoItem
    source="whatsapp_funnel_analytics"
    title="WhatsApp funnel analytics"
    :description="range?.label"
    height="fit-content"
  >
    <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

    <BarChart
      v-else-if="chartCategories && chartData"
      id="whatsapp_funnel_analytics"
      :categories="chartCategories"
      :series-data="chartData"
    />

    <EmptyScreen
      v-else
      source="whatsapp_funnel_analytics"
      text="No data found!"
      :type="emptyScreenTypes.CHART_AND_TABLE"
    />
  </ContainerInfoItem>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import BarChart from "@/src/components/charts/BarChart.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";
import { marketingWhatsappFunnelStages } from "@/src/constants/analyticConstants.js";

const range = computed(() => store.getters["analytics/getRange"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const toFetchWhatsappFunnelAnalytics = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_date: range.value?.start,
      end_date: range.value?.end,
      channel_type: "WHATSAPP",
    };

    const response = await AnalyticService.V2.fetchWorkspaceMarketingCampaignFunnel(params);
    let data = null;

    if (response) {
      if (response.success && response.data) {
        data = response.data.data || response.data;
      } else if (response.sent !== undefined || response.delivered !== undefined) {
        data = response;
      }
    }

    if (
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      (data.sent !== undefined || data.delivered !== undefined)
    ) {
      chartCategories.value = marketingWhatsappFunnelStages.map((stage) => stage.label);
      chartData.value = [
        {
          name: "WhatsApp",
          data: marketingWhatsappFunnelStages.map((stage) => data[stage.key] || 0),
        },
      ];

      const allZeros = chartData.value[0].data.every((value) => value === 0);
      if (allZeros) {
        chartCategories.value = null;
        chartData.value = null;
      }
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching whatsapp funnel analytics: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(
  [range],
  () => {
    if (range.value?.start && range.value?.end) {
      toFetchWhatsappFunnelAnalytics();
    }
  },
  { deep: true, immediate: true }
);

  useAnalyticsRefresh(toFetchWhatsappFunnelAnalytics, [range, userIds, teamIds]);
</script>

